import React, { useId, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GitHubIcon from "@mui/icons-material/GitHub";
import LaunchIcon from "@mui/icons-material/Launch";
import ActionButton from "./ActionButton";
import TagList from "./TagList";

const COLLAPSED_HEIGHT = 76;

/**
 * The one project card. Replaces three near-identical implementations that were
 * inlined in Homepage.jsx, Projects.jsx and Components/ProjectCard.jsx.
 */
const ProjectCard = ({ project }) => {
  const [expanded, setExpanded] = useState(false);
  const summaryId = useId();
  const { title, Icon, summary, tech, image, imageFit, date, github, live } =
    project;

  return (
    <Paper
      component="article"
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        height: "100%",
        borderRadius: 2.5,
        overflow: "hidden",
        border: theme.tokens.hairline,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.tokens.cardShadow,
        transition: theme.transitions.create(["box-shadow", "transform"], {
          duration: 250,
        }),
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: theme.tokens.cardShadowHover,
        },
        "&:hover .project-card__image": { transform: "scale(1.06)" },
      })}
    >
      {image && (
        <Box
          sx={{
            position: "relative",
            width: "100%",
            aspectRatio: "16 / 9",
            overflow: "hidden",
            backgroundColor: "action.hover",
          }}
        >
          <Box
            component="img"
            className="project-card__image"
            src={image}
            alt={`${title} screenshot`}
            loading="lazy"
            decoding="async"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: imageFit || "cover",
              display: "block",
              transition: "transform 0.4s ease",
            }}
          />
          {date && (
            <Typography
              variant="caption"
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                px: 1,
                py: 0.25,
                borderRadius: 1,
                fontWeight: 500,
                color: "#fff",
                backgroundColor: "rgba(0, 0, 0, 0.55)",
                backdropFilter: "blur(4px)",
              }}
            >
              {date}
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{ p: 2, display: "flex", flexDirection: "column", flex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          {Icon && <Icon fontSize="small" aria-hidden />}
          <Typography variant="h3">
            {title}
          </Typography>
        </Box>

        <Box sx={{ position: "relative" }}>
          <Collapse in={expanded} collapsedSize={COLLAPSED_HEIGHT}>
            <Typography
              id={summaryId}
              variant="body2"
              color="text.secondary"
            >
              {summary}
            </Typography>
          </Collapse>

          {/* Fade the clipped text out instead of slicing it mid-line. */}
          {!expanded && (
            <Box
              aria-hidden
              sx={(theme) => ({
                position: "absolute",
                inset: "auto 0 0 0",
                height: 32,
                pointerEvents: "none",
                backgroundImage: `linear-gradient(to bottom, transparent, ${theme.palette.background.paper})`,
              })}
            />
          )}
        </Box>

        <Button
          size="small"
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          aria-controls={summaryId}
          endIcon={
            <ExpandMoreIcon
              sx={{
                transition: "transform 0.25s ease",
                transform: expanded ? "rotate(180deg)" : "none",
              }}
            />
          }
          sx={{
            alignSelf: "flex-start",
            mt: 0.5,
            mb: 1.5,
            px: 0,
            minWidth: 0,
            color: "text.secondary",
            "&:hover": { backgroundColor: "transparent", color: "text.primary" },
          }}
        >
          {expanded ? "Read less" : "Read more"}
        </Button>

        <TagList tags={tech} size="small" sx={{ mb: 2 }} />

        {(github || live) && (
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: "auto" }}>
            {github && (
              <ActionButton
                href={github}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<GitHubIcon />}
                aria-label={`${title} source code on GitHub`}
              >
                GitHub
              </ActionButton>
            )}
            {live && (
              <ActionButton
                tone="subtle"
                href={live}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<LaunchIcon />}
                aria-label={`Open the live ${title} site`}
              >
                Live
              </ActionButton>
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default ProjectCard;
